import "@hurdl/contracts";
import { ApiRequestError } from "@hurdl/errors";
//#region src/factory/create-mutator.ts
/**
* Builds the Orval mutator, which is the seam between a Generated_Client and the
* transport.
*
* **The transport is stripped from the options before the request is issued**, so it
* never reaches `fetch`. It arrives as a property of each operation's options rather
* than as module-level state, which is what lets one page hold two stores against
* two mount points, and stripping it is what keeps that arrangement from putting a
* function into a `RequestInit`.
*
* A capability's `src/frontend/api/request.ts` becomes one call to this factory,
* exporting the result as `sendRequest`. The module stays because the generated tree
* imports it by path and pointing Orval at a path inside `node_modules` would be
* both fragile and unable to carry the message above.
*
* @throws Error when no transport was supplied, which is a wiring defect rather than
*   a request failure and so carries no status and no platform error code. Orval
*   types the options as optional and spreads them, so an operation called with none
*   typechecks; see {@link TransportOptions.transport}.
*/
function createMutator(options) {
	const { absentTransportMessage } = options;
	return async function sendRequest(url, operationOptions) {
		const { transport, ...init } = operationOptions;
		if (transport === void 0) throw new Error(absentTransportMessage);
		return await transport(url, init);
	};
}
//#endregion
//#region src/services/read-envelope.ts
/**
* The message a successful response carrying no Response_Envelope is reported
* under.
*
* It names no capability, no route and no field, which is R1.11 and is the one
* respect in which this reader differs from the two it replaces: each of those
* named its own API — "The accounts API answered…" — and a Platform Package cannot.
* `capabilities/accounts/test/accounts-store.test.ts` asserts this sentence
* verbatim, so it is a contract rather than a convenience, and the R1.14 exemption
* it takes is recorded in `AGENTS.md` beside invariant 4.
*/
const ABSENT_ENVELOPE_MESSAGE = "The API answered a successful status with no `data` envelope.";
/**
* Reads the payload out of the Response_Envelope a successful response carries.
*
* `@hurdl/http`'s `sendData` writes `{ data }` and every route of every capability
* answers through it, so the payload is one level down from the body. **This is the
* one place that level is crossed.** A Generated_Client's operations are typed with
* Orval's own `{ data, status, headers }` triple, which is what Orval's fetch client
* returns and not what a mutator's transport resolves with, so the types cannot
* describe the read and a cast is unavoidable. Confining it here is what keeps it to
* one: the reader each store carried before this package existed is deleted, not
* moved.
*
* Three answers, and the middle one is the reason the envelope is checked rather
* than assumed:
*
* - **An empty body resolves `undefined`.** A status admitting no body has none to
*   read — the Authentication_Capability's `POST /logout` answers 204 and its
*   generated operation is typed `void` — and raising on the parse would report a
*   correct response as a defect.
* - **A `data` of `null` resolves `null`.** It is a legitimate payload: that same
*   capability's `GET /email/change` answers one where no change is pending, and
*   reading it as an absence would make "no envelope" and "nothing pending"
*   indistinguishable.
* - **A body carrying no `data` property is a defect**, and so is a body that is not
*   JSON at all. Neither has an innocent reading: the status said there was a
*   payload. Both raise a plain `Error`, because a defect in an API or in a
*   transport is not a request failure and carries no status and no platform error
*   code — which is what distinguishes it from the `ApiRequestError` a refusal
*   raises.
*
* @throws Error where a successful response carried no `data` envelope.
*/
async function readEnvelope(response) {
	const text = await response.text();
	if (text === "") return;
	let body;
	try {
		body = JSON.parse(text);
	} catch {
		throw new Error(ABSENT_ENVELOPE_MESSAGE);
	}
	if (typeof body !== "object" || body === null || !("data" in body)) throw new Error(ABSENT_ENVELOPE_MESSAGE);
	return body.data;
}
//#endregion
//#region src/services/resolve-url.ts
/**
* Joins a base address and an operation path with exactly one separator,
* retaining the base path prefix and the path's query string.
*
* Handles a base address that is relative or absolute, with or without a trailing
* separator, and a path with or without a leading one. The base address is the
* application's — it is where the application chose to mount the router — so
* nothing here names a path of any capability's own.
*
* Character for character the function both existing capabilities carried, which
* is what makes this an extraction rather than a rewrite.
*/
function resolveUrl(baseUrl, path) {
	const base = baseUrl.replace(/\/+$/, "");
	const suffix = path.replace(/^\/+/, "");
	if (suffix === "") return base;
	return `${base}/${suffix}`;
}
//#endregion
//#region src/services/to-request-error.ts
/** The code a failing response carrying no usable envelope is reported under. */
const FALLBACK_CODE = "REQUEST_FAILED";
/**
* The mapped error for a refused request, read from the Response_Envelope
* `@hurdl/http`'s `sendError` writes.
*
* A response whose body is not that envelope — a proxy or gateway failure, or an
* authenticating seam of the application's answering 401 in its own words — carries
* `REQUEST_FAILED` and a message naming the status, so a caller is raised the same
* class of error either way and can always read a `code`.
*
* `ApiError` is read from `@hurdl/contracts` and declared nowhere here. The deleted
* `platform/api` scaffold declared its own as `{ status, message }` where the real
* one is `{ code, message }`, and inheriting that contradiction is what
* `governance/adr/0008-the-account-profile-and-application-declared-fields.md`
* forbids of this package.
*/
async function toRequestError(response) {
	let error;
	try {
		({error} = await response.json());
	} catch {}
	return new ApiRequestError(response.status, error?.code ?? FALLBACK_CODE, error?.message ?? `Request failed with status ${String(response.status)}`);
}
//#endregion
//#region src/factory/create-request-transport.ts
/** The status at and above which a response is a failure rather than a payload. */
const FAILURE_STATUS = 400;
/**
* Builds the transport a capability's Generated_Client issues every request
* through.
*
* The returned function resolves the address, issues the request, and then either
* raises `ApiRequestError` — for any status at or above 400, carrying the response
* envelope's own `code` and `message` — or reads the payload out of the `{ data }`
* envelope a success carries.
*
* **`credentials` is spread in only where the caller named one.** An absent option
* leaves the `RequestInit` the generated operation described exactly as it arrived,
* which is what the Accounts_Capability requires and what two of its cases assert.
* See {@link RequestTransportOptions.credentials}.
*
* **`responseFormat` is stripped in the same way the transport itself is.** It rides
* on the operation's options — the caller's per-operation statement of how the body
* is read — and is destructured out before the request reaches `fetch`, so no
* non-standard property leaks into the `RequestInit`. Absent, the request is
* unchanged and the envelope reader runs; present as `'bytes'`, a success resolves
* `await response.blob()` and no `data` property is looked for. A failure still maps
* to `ApiRequestError` regardless, because the byte path answers a body, not a
* refusal. The decision is the caller's and no response header is read; see
* {@link RequestTransport}.
*
* `globalThis.fetch` is read at call time rather than bound at construction, so a
* check substituting the global sees its substitute used.
*/
function createRequestTransport(options) {
	const { baseUrl, credentials, send } = options;
	return async function transport(path, operationInit) {
		const { responseFormat, ...init } = operationInit;
		const response = await (send ?? globalThis.fetch.bind(globalThis))(resolveUrl(baseUrl, path), credentials === void 0 ? init : {
			...init,
			credentials
		});
		if (response.status >= FAILURE_STATUS) throw await toRequestError(response);
		if (responseFormat === "bytes") return await response.blob();
		return await readEnvelope(response);
	};
}
//#endregion
export { createMutator, createRequestTransport };

//# sourceMappingURL=index.mjs.map